import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Checkbox,
  Paper,
  Popover,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  Link,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { cloneCatalog, deleteCatalog, listCatalogs } from '../../app/api';
import { useTranslation } from '../../app/LanguageContext';
import { useWorkspace } from '../../app/WorkspaceContext';
import { getUserPreferences, updateUserPreferences } from '../../app/userPreferences';
import ConfirmDialog from '../../modules/core/public/ConfirmDialog';
import ListToolbar from '../../modules/core/public/ListToolbar';
import {
  CATALOG_STATUS_CODES,
  catalogStatusColors,
  catalogStatusI18nKey,
} from './catalogStatuses';

const STATUS_FILTER_PREF_KEY = 'catalogs.status_filter';
const LIST_SORT_PREF_KEY = 'catalogs.list_sort';
const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_ROWS_PER_PAGE = 25;

const SORT_COLUMNS = [
  { id: 'id', labelKey: 'catalog.list_sort_code' },
  { id: 'name', labelKey: 'catalog.list_sort_name' },
  { id: 'status', labelKey: 'catalog.list_sort_status' },
  { id: 'updated_at', labelKey: 'catalog.list_sort_updated' },
];

const ALLOWED_SORT_FIELDS = new Set(['id', 'name', 'status', 'updated_at', 'created_at']);

const parseStoredFilter = (stored) => {
  if (!stored) return null;
  try {
    const arr = JSON.parse(stored);
    if (!Array.isArray(arr)) return null;
    return arr;
  } catch {
    return null;
  }
};

const serializeFilter = (arr) => {
  if (arr === null) return '';
  return JSON.stringify(arr);
};

const parseStoredListSort = (stored) => {
  if (!stored || typeof stored !== 'string') {
    return { sort: 'updated_at', order: 'desc' };
  }
  try {
    const obj = JSON.parse(stored);
    if (!obj || typeof obj !== 'object') {
      return { sort: 'updated_at', order: 'desc' };
    }
    const sort = typeof obj.sort === 'string' ? obj.sort : 'updated_at';
    const order = obj.order === 'asc' || obj.order === 'desc' ? obj.order : 'desc';
    if (!ALLOWED_SORT_FIELDS.has(sort)) {
      return { sort: 'updated_at', order: 'desc' };
    }
    return { sort, order };
  } catch {
    return { sort: 'updated_at', order: 'desc' };
  }
};

const serializeListSort = (s) => JSON.stringify({ sort: s.sort, order: s.order });

const formatListUpdatedAt = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const CatalogsPage = () => {
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const { currentWorkspace } = useWorkspace();

  const [catalogs, setCatalogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [duplicating, setDuplicating] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [selectedStatuses, setSelectedStatuses] = useState(null);
  const [statusFilterPrefsReady, setStatusFilterPrefsReady] = useState(false);
  const [statusFilterAnchor, setStatusFilterAnchor] = useState(null);
  const [listSort, setListSort] = useState({ sort: 'updated_at', order: 'desc' });
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const savePrefsTimeout = useRef(null);
  const saveListSortTimeout = useRef(null);
  const searchDebounceRef = useRef(null);

  const allCatalogValues = useMemo(() => [...CATALOG_STATUS_CODES], []);

  const statusOptions = useMemo(
    () =>
      CATALOG_STATUS_CODES.map((value) => ({
        value,
        label: translate(catalogStatusI18nKey(value)),
      })),
    [translate],
  );

  const effectiveSelected =
    selectedStatuses === null ? [...allCatalogValues] : [...selectedStatuses];

  const allChecked =
    allCatalogValues.length > 0 &&
    allCatalogValues.length === effectiveSelected.length &&
    allCatalogValues.every((v) => effectiveSelected.includes(v));

  const isExplicitFullCatalog =
    selectedStatuses !== null &&
    allCatalogValues.length > 0 &&
    selectedStatuses.length === allCatalogValues.length &&
    allCatalogValues.every((v) => selectedStatuses.includes(v));

  const isFilterActive = selectedStatuses !== null && !isExplicitFullCatalog;

  const listLoading = !statusFilterPrefsReady || loading;

  useEffect(() => {
    if (!currentWorkspace?.id) {
      setStatusFilterPrefsReady(true);
      setSelectedStatuses(null);
      setListSort({ sort: 'updated_at', order: 'desc' });
      setCatalogs([]);
      setTotal(0);
      return;
    }
    let cancelled = false;
    setStatusFilterPrefsReady(false);
    setSelectedStatuses(null);
    setListSort({ sort: 'updated_at', order: 'desc' });
    (async () => {
      try {
        const res = await getUserPreferences(currentWorkspace.id);
        if (!cancelled) {
          const prefs = res.data?.preferences || {};
          setSelectedStatuses(parseStoredFilter(prefs[STATUS_FILTER_PREF_KEY] || ''));
          setListSort(parseStoredListSort(prefs[LIST_SORT_PREF_KEY] || ''));
        }
      } catch (_) {
      } finally {
        if (!cancelled) setStatusFilterPrefsReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentWorkspace?.id]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchInput]);

  useEffect(() => {
    setPage(0);
  }, [selectedStatuses, listSort.sort, listSort.order, debouncedSearch]);

  useEffect(() => {
    if (total <= 0) return;
    const maxPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [total, rowsPerPage, page]);

  const saveStatusFilter = (arr) => {
    if (!currentWorkspace?.id) return;
    clearTimeout(savePrefsTimeout.current);
    savePrefsTimeout.current = setTimeout(() => {
      updateUserPreferences(currentWorkspace.id, {
        [STATUS_FILTER_PREF_KEY]: serializeFilter(arr),
      }).catch(() => {});
    }, 500);
  };

  const saveListSortPreference = (next) => {
    if (!currentWorkspace?.id) return;
    clearTimeout(saveListSortTimeout.current);
    saveListSortTimeout.current = setTimeout(() => {
      updateUserPreferences(currentWorkspace.id, {
        [LIST_SORT_PREF_KEY]: serializeListSort(next),
      }).catch(() => {});
    }, 500);
  };

  const applySelection = (next) => {
    const nextArr = [...next];
    const isFull =
      allCatalogValues.length > 0 &&
      nextArr.length === allCatalogValues.length &&
      allCatalogValues.every((v) => nextArr.includes(v));
    if (isFull) {
      setSelectedStatuses(null);
      saveStatusFilter(null);
      return;
    }
    setSelectedStatuses(nextArr);
    saveStatusFilter(nextArr);
  };

  const toggleStatusAll = () => {
    if (allCatalogValues.length === 0) return;
    if (allChecked) {
      applySelection([]);
    } else {
      applySelection(allCatalogValues);
    }
  };

  const toggleStatusOne = (s) => {
    const current = effectiveSelected;
    const next = current.includes(s) ? current.filter((x) => x !== s) : [...current, s];
    applySelection(next);
  };

  const load = useCallback(async () => {
    if (!statusFilterPrefsReady || !currentWorkspace?.id) return;
    setLoading(true);
    try {
      const q = {
        page,
        page_size: rowsPerPage,
        sort: listSort.sort,
        order: listSort.order,
      };
      if (selectedStatuses === null) {
      } else if (selectedStatuses.length === 0) {
        q.status_filter = 'none';
      } else {
        q.status = selectedStatuses;
      }
      if (debouncedSearch) q.q = debouncedSearch;

      const res = await listCatalogs(q);
      const data = res?.data ?? res;
      const list = data?.catalogs ?? (Array.isArray(data) ? data : []);
      setCatalogs(Array.isArray(list) ? list : []);
      setTotal(typeof data?.total === 'number' ? data.total : list.length);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [
    statusFilterPrefsReady,
    currentWorkspace?.id,
    selectedStatuses,
    debouncedSearch,
    page,
    rowsPerPage,
    listSort.sort,
    listSort.order,
  ]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSort = (columnId) => {
    setListSort((prev) => {
      const next =
        prev.sort === columnId
          ? { sort: columnId, order: prev.order === 'asc' ? 'desc' : 'asc' }
          : { sort: columnId, order: 'asc' };
      saveListSortPreference(next);
      return next;
    });
  };

  const handleDuplicate = async (id) => {
    setDuplicating(id);
    try {
      const res = await cloneCatalog(id);
      const data = res?.data ?? res;
      const created = data?.catalog || data;
      if (created?.id) {
        navigate(`/catalogs/${created.id}/edit`);
      } else {
        await load();
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setDuplicating(null);
    }
  };

  const handleDeleteConfirm = async () => {
    const row = pendingDelete;
    setPendingDelete(null);
    if (!row?.id) return;
    setDeleting(row.id);
    try {
      await deleteCatalog(row.id);
      setSnackbar({ open: true, message: translate('catalog.deleted'), severity: 'success' });
      await load();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setDeleting(null);
    }
  };

  const renderStatusChip = (status) => {
    const code = (status || 'DRAFT').toUpperCase();
    return (
      <Chip
        size="small"
        label={translate(catalogStatusI18nKey(code))}
        color={catalogStatusColors[code] || 'default'}
      />
    );
  };

  const renderSortHeader = (column) => (
    <TableCell key={column.id} sortDirection={listSort.sort === column.id ? listSort.order : false}>
      <TableSortLabel
        active={listSort.sort === column.id}
        direction={listSort.sort === column.id ? listSort.order : 'asc'}
        onClick={() => handleSort(column.id)}
      >
        {translate(column.labelKey)}
      </TableSortLabel>
    </TableCell>
  );

  const renderStatusFilterPopover = () => (
    <Popover
      open={Boolean(statusFilterAnchor)}
      anchorEl={statusFilterAnchor}
      onClose={() => setStatusFilterAnchor(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <Box sx={{ minWidth: 220 }}>
        <List dense disablePadding>
          <ListItemButton onClick={toggleStatusAll} dense>
            <Checkbox
              edge="start"
              checked={allChecked}
              indeterminate={effectiveSelected.length > 0 && !allChecked}
              tabIndex={-1}
              disableRipple
              size="small"
            />
            <ListItemText
              primary={translate('common.all')}
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItemButton>
          <Divider />
          {statusOptions.map(({ value, label }) => (
            <ListItemButton key={value} onClick={() => toggleStatusOne(value)} dense>
              <Checkbox
                edge="start"
                checked={effectiveSelected.includes(value)}
                tabIndex={-1}
                disableRipple
                size="small"
              />
              <ListItemText primary={label} />
            </ListItemButton>
          ))}
        </List>
      </Box>
    </Popover>
  );

  const renderRowActions = (row) => (
    <>
      <Tooltip title={translate('catalog.edit')}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/catalogs/${row.id}/edit`);
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {row.status !== 'DELETED' && (
        <Tooltip title={translate('catalog.duplicate')}>
          <span>
            <IconButton
              size="small"
              disabled={duplicating === row.id}
              onClick={(e) => {
                e.stopPropagation();
                void handleDuplicate(row.id);
              }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {row.status !== 'DELETED' && (
        <Tooltip title={translate('common.delete')}>
          <span>
            <IconButton
              size="small"
              color="error"
              disabled={deleting === row.id}
              onClick={(e) => {
                e.stopPropagation();
                setPendingDelete({ id: row.id, name: row.name });
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </>
  );

  const renderPagination = () => (
    <TablePagination
      component="div"
      count={total}
      page={page}
      onPageChange={(_, nextPage) => setPage(nextPage)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
      }}
      rowsPerPageOptions={[10, 25, 50, 100]}
      labelRowsPerPage={translate('common.rows_per_page') || 'Por página'}
    />
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/catalogs/new')}
          data-testid="catalogs-new"
        >
          {translate('catalog.create')}
        </Button>
      </Box>

      <ListToolbar
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchHelperTextKey="catalog.list_search_hint"
      />

      {isMobile ? (
        <>
          <Box sx={{ mb: 1.5 }}>
            <Button
              size="small"
              startIcon={isFilterActive ? <FilterListOffIcon /> : <FilterListIcon />}
              color={isFilterActive ? 'primary' : 'inherit'}
              variant={isFilterActive ? 'outlined' : 'text'}
              onClick={(e) => setStatusFilterAnchor(e.currentTarget)}
            >
              {translate('catalog.column_status')}
              {isFilterActive ? ` (${effectiveSelected.length})` : ''}
            </Button>
          </Box>

          {listLoading ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <CircularProgress />
            </Paper>
          ) : catalogs.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">{translate('catalog.empty_list')}</Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {catalogs.map((row) => (
                <Paper
                  key={row.id}
                  sx={{ p: 1.5, cursor: 'pointer' }}
                  onClick={() => navigate(`/catalogs/${row.id}/edit`)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {row.id}
                      </Typography>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {row.name?.trim() ? row.name : '—'}
                      </Typography>
                      <Box sx={{ mt: 0.75 }}>{renderStatusChip(row.status)}</Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {formatListUpdatedAt(row.updated_at ?? row.updatedAt)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                      {renderRowActions(row)}
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
          {!listLoading && catalogs.length > 0 && (
            <Paper sx={{ mt: 1 }}>{renderPagination()}</Paper>
          )}
        </>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {SORT_COLUMNS.slice(0, 2).map((col) => renderSortHeader(col))}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TableSortLabel
                      active={listSort.sort === 'status'}
                      direction={listSort.sort === 'status' ? listSort.order : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      {translate('catalog.column_status')}
                    </TableSortLabel>
                    {isFilterActive && (
                      <Chip
                        label={effectiveSelected.length}
                        size="small"
                        color="primary"
                        sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, cursor: 'pointer' }}
                        onClick={(e) => setStatusFilterAnchor(e.currentTarget)}
                      />
                    )}
                    <Tooltip
                      title={
                        isFilterActive
                          ? translate('catalog.filter_active')
                          : translate('catalog.filter_status')
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => setStatusFilterAnchor(e.currentTarget)}
                        sx={{ p: 0.25 }}
                        color={isFilterActive ? 'primary' : 'default'}
                      >
                        {isFilterActive ? (
                          <FilterListOffIcon fontSize="small" />
                        ) : (
                          <FilterListIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                {renderSortHeader(SORT_COLUMNS[3])}
                <TableCell align="right">{translate('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    {translate('common.loading')}
                  </TableCell>
                </TableRow>
              ) : catalogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    {translate('catalog.empty_list')}
                  </TableCell>
                </TableRow>
              ) : (
                catalogs.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/catalogs/${row.id}/edit`)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {row.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/catalogs/${row.id}/edit`}
                        underline="hover"
                        color="inherit"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {row.name?.trim() ? row.name : '—'}
                      </Link>
                    </TableCell>
                    <TableCell>{renderStatusChip(row.status)}</TableCell>
                    <TableCell>{formatListUpdatedAt(row.updated_at ?? row.updatedAt)}</TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      {renderRowActions(row)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {renderPagination()}
        </TableContainer>
      )}

      {renderStatusFilterPopover()}

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title={translate('common.confirm_delete_title')}
        message={translate('catalog.confirm_delete')}
        onConfirm={() => void handleDeleteConfirm()}
        onCancel={() => setPendingDelete(null)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CatalogsPage;
